import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, GraduationCap, Building2, Droplet, Zap, Briefcase, Users, FileText } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: Heart,
      iconColor: 'bg-[#3B5FA9]',
      title: 'Health Services',
      description: 'Improving access to quality healthcare through health center upgrades, medical supply distribution, and maternal & child health programs.',
      initiatives: [
        'Health center infrastructure improvements',
        'Medical equipment and supplies',
        'Maternal and child health programs',
        'Community health worker training'
      ]
    },
    {
      id: 2,
      icon: GraduationCap,
      iconColor: 'bg-[#F5B700]',
      title: 'Education Services',
      description: 'Enhancing educational opportunities through school infrastructure development, teacher support, and student assistance programs.',
      initiatives: [
        'School building construction and repairs',
        'Teacher housing and training',
        'Student scholarship programs',
        'Educational materials and resources'
      ]
    },
    {
      id: 3,
      icon: Building2,
      iconColor: 'bg-[#3B5FA9]',
      title: 'Infrastructure Development',
      description: 'Building essential infrastructure including roads, bridges, community facilities, and public buildings to improve connectivity and access.',
      initiatives: [
        'Road construction and maintenance',
        'Bridge construction',
        'Community hall development',
        'Government office facilities'
      ]
    },
    {
      id: 4,
      icon: Droplet,
      iconColor: 'bg-[#F5B700]',
      title: 'Water & Sanitation',
      description: 'Providing clean water access and improved sanitation facilities to promote public health and community wellbeing.',
      initiatives: [
        'Water supply systems',
        'Sanitation facilities',
        'Hygiene education programs',
        'Water source protection'
      ]
    },
    {
      id: 5,
      icon: Zap,
      iconColor: 'bg-[#3B5FA9]',
      title: 'Energy & Utilities',
      description: 'Supporting access to reliable electricity and communication services for communities across the district.',
      initiatives: [
        'Rural electrification projects',
        'Solar power installations',
        'Communication infrastructure',
        'Power line extensions'
      ]
    },
    {
      id: 6,
      icon: Briefcase,
      iconColor: 'bg-[#F5B700]',
      title: 'Economic Development',
      description: 'Promoting local economic growth through small business support, agricultural programs, and livelihood development.',
      initiatives: [
        'Small business grants and training',
        'Agricultural extension services',
        'Market facility development',
        'Youth employment programs'
      ]
    },
    {
      id: 7,
      icon: Users,
      iconColor: 'bg-[#3B5FA9]',
      title: 'Community Development',
      description: 'Strengthening communities through social programs, cultural preservation, and capacity building initiatives.',
      initiatives: [
        "Women's empowerment programs",
        'Youth development initiatives',
        'Cultural heritage preservation',
        'Community organization support'
      ]
    },
    {
      id: 8,
      icon: FileText,
      iconColor: 'bg-[#F5B700]',
      title: 'Governance & Administration',
      description: 'Supporting effective local governance through capacity building, administrative support, and transparency initiatives.',
      initiatives: [
        'Local-level government support',
        'Administrative capacity building',
        'Financial management training',
        'Transparency and accountability programs'
      ]
    }
  ];

  const deliverySteps = [
    {
      number: '1',
      title: 'Community Consultation',
      description: 'Engage with communities to identify needs and priorities'
    },
    {
      number: '2',
      title: 'Strategic Planning',
      description: 'Develop plans aligned with district development goals'
    },
    {
      number: '3',
      title: 'Implementation',
      description: 'Execute projects with quality and efficiency'
    },
    {
      number: '4',
      title: 'Monitoring & Evaluation',
      description: 'Track progress and measure impact'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Our Services</h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Comprehensive services designed to improve the lives of all Hiri Koiari District residents
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-gray-300 hover:shadow-xl transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className={`${service.iconColor} w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{service.description}</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Key Initiatives:</h4>
                    <ul className="space-y-2">
                      {service.initiatives.map((initiative, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                          <span className="text-gray-600">{initiative}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Delivery Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Our Service Delivery Approach
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {deliverySteps.map((step, index) => (
              <div
                key={step.number}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-shadow"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-16 h-16 bg-[#3B5FA9] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Need Assistance Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Need Assistance?</h2>
          <p className="text-lg text-gray-600 mb-8">
            If you have questions about our services or need support, please don't hesitate to contact us.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-[#3B5FA9] hover:bg-[#2E4A8B] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
